import React, { useEffect, useState } from 'react';
import { useWedding } from '../context/WeddingContext';
import { getTranslation } from '../i18n';
import { getAuthenticatedSupabase } from '../supabaseClient';
import { CeremonyMeta, CeremonyRole, CeremonyStep } from '../types';
import { Modal } from '../components/Modal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Toast, ToastMessage } from '../components/Toast';
import { ScrollText, Plus, Trash2, Edit2, Music, PlusCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';

export default function CeremonyPage() {
  const { wedding, locale } = useWedding();
  const t = (key: string) => getTranslation(locale, key);

  const [meta, setMeta] = useState<Partial<CeremonyMeta>>({});
  const [steps, setSteps] = useState<CeremonyStep[]>([]);
  const [roles, setRoles] = useState<CeremonyRole[]>([]);
  
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [formData, setFormData] = useState<any>({});
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteType, setDeleteType] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const fetchData = async () => {
    if (!wedding) return;
    const supabase = getAuthenticatedSupabase();
    const [m, s, r] = await Promise.all([
      supabase.from('ceremony_meta').select('*').eq('wedding_id', wedding.id).single(),
      supabase.from('ceremony_steps').select('*').eq('wedding_id', wedding.id),
      supabase.from('ceremony_roles').select('*').eq('wedding_id', wedding.id)
    ]);
    if (!m.error) setMeta(m.data ?? {});
    if (!s.error && Array.isArray(s.data)) setSteps(s.data);
    if (!r.error && Array.isArray(r.data)) setRoles(r.data);
  };

  useEffect(() => {
    fetchData();
  }, [wedding]);

  const openModal = (type: string, data: any = {}) => {
    setActiveModal(type);
    setFormData({ ...data, wedding_id: wedding?.id });
  };

  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ id: String(Date.now()), type, text });
    setTimeout(() => setToast(null), 4000);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const supabase = getAuthenticatedSupabase();
    const table = activeModal === 'step' ? 'ceremony_steps' : activeModal === 'role' ? 'ceremony_roles' : 'ceremony_meta';
    
    if (activeModal === 'meta') {
      if (meta.id) {
        const { data, error } = await supabase.from('ceremony_meta').update(formData).eq('id', meta.id).select().single();
        if (error) {
          showToast('error', `Chyba: ${error.message}`);
          return;
        }
        if (data) setMeta(data);
      } else {
        const { data, error } = await supabase.from('ceremony_meta').insert(formData).select().single();
        if (error) {
          showToast('error', `Chyba: ${error.message}`);
          return;
        }
        if (data) setMeta(data);
      }
      showToast('success', 'Nastavení obřadu uloženo.');
    } else if (activeModal === 'step') {
      let savedStep: CeremonyStep | null = null;
      if (formData.id) {
        const { data, error } = await supabase.from(table).update(formData).eq('id', formData.id).select().single();
        if (error) {
          showToast('error', `Chyba: ${error.message}`);
          return;
        }
        if (data) savedStep = data;
        else savedStep = { ...formData } as CeremonyStep;
      } else {
        const { data, error } = await supabase.from(table).insert(formData).select().single();
        if (error) {
          showToast('error', `Chyba: ${error.message}`);
          return;
        }
        if (data) savedStep = data;
      }
      if (savedStep?.id && wedding?.id) {
        const startTime = (savedStep as any).start_time || formData.start_time || '12:00';
        const title = (savedStep as any).step_name || formData.step_name || '';
        const { data: existing } = await supabase.from('schedule_items').select('id').eq('ceremony_step_id', savedStep.id).maybeSingle();
        if (existing) {
          await supabase.from('schedule_items').update({ title, start_time: startTime }).eq('id', existing.id);
        } else {
          await supabase.from('schedule_items').insert({
            wedding_id: wedding.id,
            title,
            start_time: startTime,
            ceremony_step_id: savedStep.id,
            is_public: true,
          });
        }
      }
      if (savedStep) {
        const stepWithTime = { ...savedStep, start_time: (savedStep as any).start_time ?? formData.start_time };
        setSteps(prev => {
          const idx = prev.findIndex(x => x.id === stepWithTime.id);
          const next = [...prev];
          if (idx >= 0) next[idx] = { ...next[idx], ...stepWithTime };
          else next.push(stepWithTime as CeremonyStep);
          return next.sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
        });
        showToast('success', 'Krok uložen.');
      }
    } else {
      const { error } = formData.id
        ? await supabase.from(table).update(formData).eq('id', formData.id)
        : await supabase.from(table).insert(formData);
      if (error) {
        showToast('error', `Chyba: ${error.message}`);
        return;
      }
      showToast('success', 'Uloženo.');
    }
    
    setActiveModal(null);
    await fetchData();
  };

  const handleDelete = async () => {
    if (deleteId && deleteType) {
      const supabase = getAuthenticatedSupabase();
      await supabase.from(deleteType).delete().eq('id', deleteId);
      setDeleteId(null);
      setDeleteType(null);
      await fetchData();
    }
  };

  if (!wedding) return null;

  return (
    <div className="space-y-12 animate-fade-in">
       <div className="flex items-center gap-4">
          <div className="bg-brand-100 dark:bg-brand-900/30 text-brand-600 p-3 rounded-2xl">
             <ScrollText size={32} />
          </div>
          <div>
             <h1 className="text-ds-h1 text-slate-900 dark:text-white">{t('ceremony.title')}</h1>
             <p className="text-ds-body text-slate-500 dark:text-slate-400">{t('ceremony.subtitle')}</p>
          </div>
       </div>

       {/* Meta Info */}
       <div id="zaklad" className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-ds-border relative group">
          <button onClick={() => openModal('meta', meta)} className="absolute top-8 right-8 p-2 bg-slate-100 dark:bg-slate-800 rounded-xl hover:text-brand-600 transition-colors"><Edit2 size={16} /></button>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
             <div>
                <h3 className="text-ds-meta text-slate-400 mb-2">{t('ceremony.meta.type')}</h3>
                <p className="text-xl font-medium">{meta.ceremony_type ? t(`ceremony.meta.${meta.ceremony_type}`) : 'Nezadáno'}</p>
             </div>
             <div>
                <h3 className="text-ds-meta text-slate-400 mb-2">{t('ceremony.meta.location')}</h3>
                <p className="text-xl font-medium">{meta.location_name || 'Nezadáno'}</p>
             </div>
             <div className="md:col-span-2">
                <h3 className="text-ds-meta text-slate-400 mb-2">{t('ceremony.meta.plan_b')}</h3>
                <p className="text-lg opacity-80">{meta.plan_b_weather || 'Není definován'}</p>
             </div>
          </div>
       </div>

       {/* Timeline / Steps */}
       <div id="prubeh" className="scroll-mt-24 space-y-6">
          <div className="flex items-center justify-between">
             <h2 className="text-ds-h2 text-slate-900 dark:text-white">{t('ceremony.sections.steps')}</h2>
             <Button onClick={() => openModal('step', { order_index: steps.length + 1 })} variant="secondary" icon={<PlusCircle size={18} />}>
                {t('common.add')}
             </Button>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400">{t('ceremony.sync_to_schedule')}</p>
          <div className="space-y-4">
             {(() => {
               const sortedSteps = [...steps].sort((a, b) => {
                 const tA = (a.start_time || '23:59').slice(0, 5);
                 const tB = (b.start_time || '23:59').slice(0, 5);
                 return tA.localeCompare(tB);
               });
               return sortedSteps.map((step, idx) => (
                <div key={step.id} className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-ds-border flex gap-6 group transition-all">
                   <div className="flex flex-col items-center gap-0">
                      <span className="text-sm font-medium text-slate-900 dark:text-white">{step.start_time ? String(step.start_time).slice(0, 5) : '–:––'}</span>
                      <span className="text-2xl font-black text-slate-200 dark:text-slate-800" aria-hidden>{(idx + 1).toString().padStart(2, '0')}</span>
                   </div>
                   <div className="flex-1">
                      <div className="flex justify-between items-start">
                         <h3 className="text-ds-h3 font-semibold mb-1">{step.step_name}</h3>
                         <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button type="button" onClick={() => openModal('step', step)} className="p-1.5 text-slate-400 hover:text-brand-600"><Edit2 size={16} /></button>
                            <button type="button" onClick={() => { setDeleteId(step.id); setDeleteType('ceremony_steps'); }} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 size={16} /></button>
                         </div>
                      </div>
                      <p className="text-slate-500 text-sm mb-3">{step.description}</p>
                      {step.cue && (
                         <div className="flex items-center gap-2 text-ds-meta text-brand-600 bg-brand-50 dark:bg-brand-900/10 px-3 py-1.5 rounded-lg w-fit">
                            <Music size={14} /> Cue: {step.cue}
                         </div>
                      )}
                   </div>
                </div>
             ));
             })()}
          </div>
       </div>

       {/* Roles */}
       <div id="kontakty" className="scroll-mt-24 space-y-6">
          <div className="flex items-center justify-between">
             <h2 className="text-ds-h2 text-slate-900 dark:text-white">{t('ceremony.sections.roles')}</h2>
             <Button onClick={() => openModal('role')} variant="secondary" icon={<PlusCircle size={18} />}>
                {t('common.add')}
             </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {roles.map(role => (
                <div key={role.id} className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-ds-border group relative">
                   <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => openModal('role', role)} className="p-1.5 text-slate-400 hover:text-brand-600"><Edit2 size={16} /></button>
                      <button onClick={() => { setDeleteId(role.id); setDeleteType('ceremony_roles'); }} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 size={16} /></button>
                   </div>
                   <h3 className="text-ds-meta text-slate-400 mb-1">{role.role_key}</h3>
                   <p className="text-lg font-medium">{role.person_name}</p>
                   {role.instructions && <p className="text-sm text-slate-500 mt-2 italic">"{role.instructions}"</p>}
                </div>
             ))}
          </div>
       </div>

       <Modal isOpen={!!activeModal} onClose={() => setActiveModal(null)} title={activeModal === 'step' ? 'Krok harmonogramu' : activeModal === 'role' ? 'Role' : 'Nastavení obřadu'}>
          <form onSubmit={handleSave} className="space-y-6">
             {activeModal === 'step' && (
                <>
                   <div>
                      <label className="text-ds-meta text-slate-500 mb-2 block">{t('ceremony.step_time')}</label>
                      <input type="time" className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none" value={formData.start_time || ''} onChange={e => setFormData({...formData, start_time: e.target.value})} />
                   </div>
                   <input placeholder={t('ceremony.step.name')} className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none" value={formData.step_name || ''} onChange={e => setFormData({...formData, step_name: e.target.value})} />
                   <textarea placeholder={t('ceremony.step.desc')} className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none" value={formData.description || ''} onChange={e => setFormData({...formData, description: e.target.value})} />
                   <input placeholder={t('ceremony.step.cue')} className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none" value={formData.cue || ''} onChange={e => setFormData({...formData, cue: e.target.value})} />
                </>
             )}
             {activeModal === 'role' && (
                <>
                   <input placeholder="Role (např. Svědek)" className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none" value={formData.role_key || ''} onChange={e => setFormData({...formData, role_key: e.target.value})} />
                   <input placeholder="Jméno osoby" className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none" value={formData.person_name || ''} onChange={e => setFormData({...formData, person_name: e.target.value})} />
                   <textarea placeholder="Instrukce" className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none" value={formData.instructions || ''} onChange={e => setFormData({...formData, instructions: e.target.value})} />
                </>
             )}
             {activeModal === 'meta' && (
                <>
                   <select className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none" value={formData.ceremony_type || ''} onChange={e => setFormData({...formData, ceremony_type: e.target.value})}>
                      <option value="civilni">Civilní</option>
                      <option value="cirkevni">Církevní</option>
                      <option value="symbolicky">Symbolický</option>
                   </select>
                   <input placeholder="Místo konání" className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none" value={formData.location_name || ''} onChange={e => setFormData({...formData, location_name: e.target.value})} />
                   <textarea placeholder="Mokrá varianta (Plán B)" className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none" value={formData.plan_b_weather || ''} onChange={e => setFormData({...formData, plan_b_weather: e.target.value})} />
                </>
             )}
             <button type="submit" className="w-full bg-brand-600 text-white font-bold py-4 rounded-xl hover:bg-brand-700 transition-colors">{t('common.save')}</button>
          </form>
       </Modal>
       <ConfirmDialog isOpen={!!deleteId} onCancel={() => setDeleteId(null)} onConfirm={handleDelete} title={t('common.delete')} description={t('common.confirm_delete')} />
       {toast && <Toast toast={toast} onClose={() => setToast(null)} />}
    </div>
  );
}